import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';

import { useQueryClient } from '@tanstack/react-query';

import {
  getListOccurrenceAttendanceQueryKey,
  useCreateAttendanceAdjustment,
  useListOccurrenceAttendance,
} from '../../api/generated/attendance/attendance';
import type { AttendanceRecord, AttendanceStatus, Me } from '../../api/generated/model';
import { Alert } from '../../design/primitives/Alert';
import { Button } from '../../design/primitives/Button';
import { Field } from '../../design/primitives/Field';
import { formatDateInPersonTimezone } from '../../i18n/datetime';
import { useGetMe } from '../../api/generated/identity/identity';
import { isReauthRequired, useReauthGate } from '../reauth/ReauthGate';
import { listState } from './listState';
import styles from './attendance.module.css';

const STATUSES: AttendanceStatus[] = ['present', 'late', 'absent', 'excused', 'cancelled_by_school'];

export function AttendanceAdjustPage() {
  const { t } = useTranslation();
  const { occurrenceId = '' } = useParams();
  const meQuery = useGetMe();
  const me = meQuery.data;
  const listQuery = useListOccurrenceAttendance(occurrenceId);

  // Mesmo tipo que a tela de leitura usa, e pela mesma razao: `data ?? []`
  // fundia "ainda carregando", "o servidor recusou" e "ninguem compareceu" numa
  // lista vazia. Aqui a fusao era pior — o formulario de correcao continuava
  // desenhado sobre a lista que nao carregou, com o seletor de pessoa vazio.
  const state = listState<AttendanceRecord>(listQuery);

  if (!me) return null;

  return (
    <div className={styles.page}>
      <h1>{t('attendance.adjustTitle')}</h1>
      {state.kind === 'loading' ? (
        <p className={styles.pending} role="status">
          {t('app.loading')}
        </p>
      ) : null}
      {state.kind === 'refused' ? (
        <Alert tone="error">
          {t('attendance.refused')}
          <p>{t('attendance.refusedDetail')}</p>
        </Alert>
      ) : null}
      {state.kind === 'failed' ? <Alert tone="error">{t('error.loadFailed')}</Alert> : null}
      {state.kind === 'empty' ? <p>{t('attendance.empty')}</p> : null}
      {state.kind === 'loaded' ? (
        <AdjustForm occurrenceId={occurrenceId} records={state.items} me={me} />
      ) : null}
    </div>
  );
}

/**
 * A correcao da presenca apurada.
 *
 * **A operacao e sensivel**: `createAttendanceAdjustment` esta registrada com
 * `Sensitive: true` em `api/internal/app/policies.go`, e `CreateSession` nao
 * grava `reauth_at` — o 403 de reautenticacao e o resultado esperado do
 * primeiro clique, e nao um acidente raro. Sem o portao, a tela engolia esse
 * 403 junto com todo o resto: o botao girava, voltava ao normal, nada era dito
 * e a correcao simplesmente nao acontecia. O portao guarda a acao recusada com
 * os valores que ja estavam no formulario e a repete apos a confirmacao.
 *
 * A alternativa recusada foi mostrar um alerta pedindo para entrar de novo:
 * ela perde o motivo digitado, que e a parte cara deste formulario.
 */
function AdjustForm({
  occurrenceId,
  records,
  me,
}: {
  readonly occurrenceId: string;
  readonly records: readonly AttendanceRecord[];
  readonly me: Me;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const gate = useReauthGate();
  const adjust = useCreateAttendanceAdjustment();
  const [status, setStatus] = useState<AttendanceStatus>('present');
  const [reason, setReason] = useState('');
  const [recordId, setRecordId] = useState('');

  // O 403 de reautenticacao ja tem resposta na tela: o dialogo. Repetir a falha
  // num alerta ao lado seria dizer duas vezes a mesma coisa.
  const unexpected = adjust.error !== null && !isReauthRequired(adjust.error);

  const submit = (): void => {
    gate.run((onReauthRequired) => {
      adjust.mutate(
        { attendanceRecordId: recordId, data: { status, reason: reason.trim() } },
        {
          onError: onReauthRequired,
          onSuccess: () => {
            // Sem isto a lista de presenca volta do cache com o estado anterior
            // por ate 30 s (`staleTime` do cliente): o operador via "(sem
            // ajuste)" logo depois de ajustar e tendia a repetir a correcao, e a
            // repeticao sobrescreve o motivo da primeira.
            void queryClient.invalidateQueries({
              queryKey: getListOccurrenceAttendanceQueryKey(occurrenceId),
            });
            void navigate('/attendance');
          },
        },
      );
    });
  };

  return (
    <>
      {gate.dialog}

      {/*
        Este `select` escolhe A PESSOA cuja presenca sera corrigida, e o nome
        que ele expunha era "Apurada / Ajustada" — os titulos das duas COLUNAS
        da comparacao logo abaixo, e nao a funcao do controle. Quem navega por
        leitor de tela ouvia dois combos com nomes sobrepostos e nenhum deles
        dizia o que se escolhia (WCAG 4.1.2). O axe passava: ele confere se ha
        nome acessivel, nao se o nome esta certo.

        A opcao vazia dizia "Nenhum registro de presenca." — a frase de lista
        VAZIA — dentro de um seletor que so e desenhado quando a lista TEM
        registros. O lugar dela e um convite a escolher, e `enrollment.choose`
        ja e exatamente isso nos tres catalogos.

        O rotulo vem do catalogo de financeiro porque "Pessoa" e a mesma
        palavra neutra nos tres idiomas e o catalogo tem dono exclusivo em
        outra onda: a chave propria (`attendance.pickPerson`) esta no
        relatorio, em `fronteira`.
      */}
      <label className={styles.selectLabel}>
        {t('finance.filter.person')}
        <select value={recordId} onChange={(e) => setRecordId(e.target.value)}>
          <option value="">{t('enrollment.choose')}</option>
          {records.map((r) => (
            <option key={r.id} value={r.id}>
              {r.person.displayName}: {t(`attendance.status.${r.computed}`)}
              {r.adjustment ? ` | ${t(`attendance.status.${r.adjustment.status}`)}` : ''}
            </option>
          ))}
        </select>
      </label>

      {records.map((r) => (
        <div key={r.id} className={styles.compare}>
          <span>
            {t('attendance.computed')}: {t(`attendance.status.${r.computed}`)}
          </span>
          <span>
            {t('attendance.adjusted')}:{' '}
            {r.adjustment ? t(`attendance.status.${r.adjustment.status}`) : t('attendance.noAdjustment')}
          </span>
          {r.adjustment ? (
            <span>
              {t('attendance.by', {
                name: r.adjustment.adjustedBy.displayName,
                date: formatDateInPersonTimezone(r.adjustment.adjustedAt, me.timezone, me.locale),
              })}
            </span>
          ) : null}
          {r.adjustment ? <p>{r.adjustment.reason}</p> : null}
        </div>
      ))}

      {/*
        O seletor do novo estado nao tinha rotulo nenhum: o leitor de tela
        anunciava "caixa de combinacao" e o valor, sem dizer o que se escolhe
        (WCAG 4.1.2). E o campo decide se a aula conta como dada, que e o que
        decide quanto o professor recebe.
      */}
      <label className={styles.selectLabel}>
        {t('attendance.adjusted')}
        <select value={status} onChange={(e) => setStatus(e.target.value as AttendanceStatus)}>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {t(`attendance.status.${s}`)}
            </option>
          ))}
        </select>
      </label>

      <Field
        label={t('attendance.reason')}
        name="reason"
        required
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />

      {unexpected ? <Alert tone="error">{t('error.unexpected')}</Alert> : null}

      <Button
        variant="primary"
        disabled={!recordId || !reason.trim()}
        loading={adjust.isPending}
        onClick={submit}
      >
        {t('action.confirm')}
      </Button>
    </>
  );
}
